import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarCheck, CheckCircle, XCircle, PaperPlaneTilt, FloppyDisk } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PushNotificationsCard } from "../components/PushNotificationsCard";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const two = (n) => String(n).padStart(2, "0");

export default function ServiziPage() {
  const [serviceSmtp, setServiceSmtp] = useState(null);
  const [smtpSending, setSmtpSending] = useState(false);
  const [sched, setSched] = useState(null);
  const [savingSched, setSavingSched] = useState(false);

  useEffect(() => {
    api.get("/admin/maintenance/service-smtp")
      .then(({ data }) => setServiceSmtp(data))
      .catch(() => {});
    api.get("/admin/maintenance/comms-schedule")
      .then(({ data }) => setSched(data))
      .catch(() => {});
  }, []);

  const handleSaveSched = async () => {
    setSavingSched(true);
    try {
      const { data } = await api.put("/admin/maintenance/comms-schedule", {
        backup: sched.backup, autoindex: sched.autoindex, report: sched.report, briefing: sched.briefing,
      });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingSched(false);
    }
  };

  const setBackup = (patch) => setSched((s) => ({ ...s, backup: { ...s.backup, ...patch } }));
  const setAutoIndex = (patch) => setSched((s) => ({ ...s, autoindex: { ...s.autoindex, ...patch } }));
  const setReport = (patch) => setSched((s) => ({ ...s, report: { ...s.report, ...patch } }));
  const setBriefing = (patch) => setSched((s) => ({ ...s, briefing: { ...s.briefing, ...patch } }));
  const [reportSending, setReportSending] = useState(false);
  const [briefingSending, setBriefingSending] = useState(false);

  const handleSendBriefing = async () => {
    setBriefingSending(true);
    try {
      const { data } = await api.post("/admin/maintenance/send-briefing");
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBriefingSending(false);
    }
  };

  const handleSendReport = async () => {
    setReportSending(true);
    try {
      const { data } = await api.post("/admin/maintenance/send-report");
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReportSending(false);
    }
  };
  const timeVal = (o) => `${two(o.hour)}:${two(o.minute)}`;
  const parseTime = (v) => {
    const [h, m] = v.split(":");
    return { hour: Math.min(23, parseInt(h, 10) || 0), minute: Math.min(59, parseInt(m, 10) || 0) };
  };

  const handleSendBackup = async () => {
    setSmtpSending(true);
    try {
      const { data } = await api.post("/admin/maintenance/send-backup");
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSmtpSending(false);
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="servizi-page">
      <div className="mb-12 animate-fade-up">
        <Link to="/impostazioni" data-testid="servizi-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors mb-3">
          <ArrowLeft size={13} /> Impostazioni
        </Link>
        <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Pannello Admin</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Servizi</h1>
        <p className="text-sm text-slate-400 mt-3">Pianificazione delle comunicazioni automatiche e notifiche push del portale.</p>
      </div>

      <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="smtp-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <CalendarCheck size={40} weight="duotone" className="text-violet-400" />
          {serviceSmtp?.configured ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400"><CheckCircle size={14} weight="fill" /> SMTP configurato</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400"><XCircle size={14} weight="fill" /> SMTP non configurato</span>
          )}
        </div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Pianificazione comunicazioni</h2>
        <p className="text-sm text-slate-400 mb-6">Scegli quali comunicazioni automatiche inviare e a che ora. Le email usano l'SMTP Comunicazioni centralizzato, configurabile nella sezione <Link to="/impostazioni" className="text-violet-300 hover:text-violet-200 underline underline-offset-2">Impostazioni</Link>.</p>

        {sched && (
          <div className="space-y-4 mb-6" data-testid="comms-schedule">
            <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 flex flex-wrap items-center gap-x-5 gap-y-3" data-testid="sched-backup-row">
              <Switch data-testid="sched-backup-enabled" checked={sched.backup.enabled} onCheckedChange={(v) => setBackup({ enabled: v })} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">Backup completo via email (BKP_Globale)</p>
                <p className="text-[11px] text-white/40">Portale + webapp con Front-End, Back-End e Database, inviato ai destinatari SMTP.</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={String(sched.backup.day)} onValueChange={(v) => setBackup({ day: parseInt(v, 10) })}>
                  <SelectTrigger data-testid="sched-backup-day" className="h-9 w-32 bg-transparent border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    {DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input type="time" data-testid="sched-backup-time" value={timeVal(sched.backup)}
                  onChange={(e) => setBackup(parseTime(e.target.value))}
                  className="h-9 px-2 rounded-md bg-transparent border border-white/10 text-white text-xs" />
                <span className="text-[10px] text-white/35">UTC</span>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 flex flex-wrap items-center gap-x-5 gap-y-3" data-testid="sched-autoindex-row">
              <Switch data-testid="sched-autoindex-enabled" checked={sched.autoindex.enabled} onCheckedChange={(v) => setAutoIndex({ enabled: v })} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">Controllo indicizzazione automatico</p>
                <p className="text-[11px] text-white/40">Ogni notte richiede a Google l'indicizzazione delle pagine escluse e ti avvisa dei progressi (push).</p>
              </div>
              <div className="flex items-center gap-2">
                <input type="time" data-testid="sched-autoindex-time" value={timeVal(sched.autoindex)}
                  onChange={(e) => setAutoIndex(parseTime(e.target.value))}
                  className="h-9 px-2 rounded-md bg-transparent border border-white/10 text-white text-xs" />
                <span className="text-[10px] text-white/35">UTC</span>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 flex flex-wrap items-center gap-x-5 gap-y-3" data-testid="sched-report-row">
              <Switch data-testid="sched-report-enabled" checked={sched.report.enabled} onCheckedChange={(v) => setReport({ enabled: v })} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">Report settimanale via email</p>
                <p className="text-[11px] text-white/40">Riepilogo di SEO, visite da Google, indicizzazione e marketing degli ultimi 7 giorni.</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={String(sched.report.day)} onValueChange={(v) => setReport({ day: parseInt(v, 10) })}>
                  <SelectTrigger data-testid="sched-report-day" className="h-9 w-32 bg-transparent border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    {DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input type="time" data-testid="sched-report-time" value={timeVal(sched.report)}
                  onChange={(e) => setReport(parseTime(e.target.value))}
                  className="h-9 px-2 rounded-md bg-transparent border border-white/10 text-white text-xs" />
                <span className="text-[10px] text-white/35">UTC</span>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 flex flex-wrap items-center gap-x-5 gap-y-3" data-testid="sched-briefing-row">
              <Switch data-testid="sched-briefing-enabled" checked={sched.briefing?.enabled ?? true} onCheckedChange={(v) => setBriefing({ enabled: v })} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">Briefing giornaliero V.I.K.I.</p>
                <p className="text-[11px] text-white/40">Ogni mattina V.I.K.I. ti scrive un punto della situazione: stato siti, alert delle ultime 24 ore, promemoria e scadenze.</p>
              </div>
              <div className="flex items-center gap-2">
                <input type="time" data-testid="sched-briefing-time" value={timeVal(sched.briefing || { hour: 7, minute: 30 })}
                  onChange={(e) => setBriefing(parseTime(e.target.value))}
                  className="h-9 px-2 rounded-md bg-transparent border border-white/10 text-white text-xs" />
                <span className="text-[10px] text-white/35">UTC</span>
              </div>
            </div>

            <Button data-testid="sched-save-button" onClick={handleSaveSched} disabled={savingSched}
              className="h-10 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-xs">
              <FloppyDisk size={14} className="mr-1.5" /> {savingSched ? "Salvataggio..." : "Salva pianificazione"}
            </Button>
          </div>
        )}

        <div className="pt-5 border-t border-white/10">
          {serviceSmtp?.configured ? (
            <div className="text-sm space-y-1.5 mb-4" data-testid="service-smtp-status">
              <p className="text-slate-400">Host: <span className="text-white/80">{serviceSmtp.host}</span> — Destinatari: <span className="text-white/80">{(serviceSmtp.recipients || []).join(", ") || "—"}</span></p>
            </div>
          ) : (
            <p className="text-sm text-amber-400 mb-4" data-testid="service-smtp-missing">
              Nessun SMTP Comunicazioni configurato. Vai in <Link to="/impostazioni" className="underline underline-offset-2">Impostazioni</Link> e compila la sezione "SMTP (Comunicazioni)".
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <Button data-testid="smtp-send-backup-button" onClick={handleSendBackup} disabled={smtpSending || !serviceSmtp?.configured} variant="ghost"
              className="h-11 px-6 border border-violet-400/40 text-violet-300 hover:bg-violet-400/10 hover:text-violet-200 rounded-full font-semibold text-sm disabled:opacity-40">
              <PaperPlaneTilt size={16} className="mr-1.5" /> {smtpSending ? "Invio backup..." : "Invia backup via email ora"}
            </Button>
            <Button data-testid="send-report-button" onClick={handleSendReport} disabled={reportSending || !serviceSmtp?.configured} variant="ghost"
              className="h-11 px-6 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 hover:text-emerald-200 rounded-full font-semibold text-sm disabled:opacity-40">
              <PaperPlaneTilt size={16} className="mr-1.5" /> {reportSending ? "Invio report..." : "Invia report ora"}
            </Button>
            <Button data-testid="send-briefing-button" onClick={handleSendBriefing} disabled={briefingSending || !serviceSmtp?.configured} variant="ghost"
              className="h-11 px-6 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 hover:text-cyan-200 rounded-full font-semibold text-sm disabled:opacity-40">
              <PaperPlaneTilt size={16} className="mr-1.5" /> {briefingSending ? "Invio briefing..." : "Invia briefing V.I.K.I. ora"}
            </Button>
          </div>
        </div>
      </div>

      <PushNotificationsCard />
    </main>
  );
}
